/**
 * Local picker recents (localStorage only — SPEC §1.2 / FR-MSG-006
 * discipline: nothing about picker usage leaves the device). Sticker
 * *pictures* are not kept here any more: see stickers/myStickers.ts.
 */

const EMOJI_RECENTS_KEY = "veto.emoji.recents";
const STICKER_RECENTS_KEY = "veto.sticker.recents";
const CUSTOM_STICKERS_KEY = "veto.stickers.custom";
const GIF_RECENTS_KEY = "veto.gif.recents";

const MAX_EMOJI_RECENTS = 24;
const MAX_STICKER_RECENTS = 16;
const MAX_GIF_RECENTS = 12;
/** Legacy imported stickers (data URLs): 64 KiB each, 16 max. */
const MAX_CUSTOM_STICKERS = 16;
const MAX_CUSTOM_STICKER_BYTES = 64 * 1024;

function readList<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeList<T>(key: string, items: T[]): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(items.slice(0, 64)));
    return true;
  } catch {
    return false;
  }
}

export function loadEmojiRecents(): string[] {
  return readList<string>(EMOJI_RECENTS_KEY).filter((e) => typeof e === "string" && e.length <= 12);
}

export function rememberEmoji(emoji: string): string[] {
  const next = [emoji, ...loadEmojiRecents().filter((e) => e !== emoji)].slice(0, MAX_EMOJI_RECENTS);
  writeList(EMOJI_RECENTS_KEY, next);
  return next;
}

export interface StickerRef {
  /** `${pack}/${id}` for built-ins, `custom:${uuid}` for imports. */
  ref: string;
}

export function loadStickerRecents(): string[] {
  return readList<string>(STICKER_RECENTS_KEY).filter(
    (r) => typeof r === "string" && /^[a-z0-9.-]+\/[a-z0-9-]+$|^custom:[a-z0-9-]{8,40}$/.test(r),
  );
}

export function rememberSticker(ref: string): string[] {
  const next = [ref, ...loadStickerRecents().filter((r) => r !== ref)].slice(0, MAX_STICKER_RECENTS);
  writeList(STICKER_RECENTS_KEY, next);
  return next;
}

/**
 * LEGACY (read-only): older builds kept imported stickers here as plain data
 * URLs. "My stickers" now live sealed in IndexedDB (stickers/myStickers.ts);
 * these entries are migrated once, then the key is removed.
 */
export interface CustomSticker {
  id: string;
  name: string;
  /** image/webp|png|gif data URL (≤64 KiB). */
  dataUrl: string;
}

export function loadCustomStickers(): CustomSticker[] {
  return readList<CustomSticker>(CUSTOM_STICKERS_KEY)
    .filter(
      (s) =>
        s &&
        typeof s.id === "string" &&
        typeof s.dataUrl === "string" &&
        s.dataUrl.startsWith("data:image/") &&
        s.dataUrl.length <= MAX_CUSTOM_STICKER_BYTES * 1.4,
    )
    .slice(0, MAX_CUSTOM_STICKERS);
}

export function hasLegacyCustomStickers(): boolean {
  try {
    return localStorage.getItem(CUSTOM_STICKERS_KEY) !== null;
  } catch {
    return false;
  }
}

export function clearLegacyCustomStickers(): void {
  try {
    localStorage.removeItem(CUSTOM_STICKERS_KEY);
  } catch {
    /* storage unavailable */
  }
}

export interface GifRecent {
  /** Proxied media URL (same-origin) — reused for quick re-send. */
  full: string;
  preview: string;
  desc: string;
}

export function loadGifRecents(): GifRecent[] {
  return readList<GifRecent>(GIF_RECENTS_KEY)
    .filter((g) => g && typeof g.full === "string" && g.full.startsWith("/api/gif/media?u=") && typeof g.preview === "string")
    .slice(0, MAX_GIF_RECENTS);
}

export function rememberGif(entry: GifRecent): GifRecent[] {
  const next = [entry, ...loadGifRecents().filter((g) => g.full !== entry.full)].slice(0, MAX_GIF_RECENTS);
  writeList(GIF_RECENTS_KEY, next);
  return next;
}
